import { DateTime } from "luxon";
import React, { useRef, useState } from "react";
import { FcPrint } from "react-icons/fc";
import { useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";

import { postGenerarEstadoDeCuentaAPI } from "../../API/ApiContratos";
import imageVitanova from "../../Images/LogotipoNegro.png";
import { AuthContext } from "../../auth/authContext";

const BRAND_COLOR = "#111111";
const fmtFecha = (iso) =>
  iso ? DateTime.fromISO(iso).toFormat("dd-MM-yyyy") : "—";
const fmtMonto = (monto) =>
  `$${parseFloat(monto || 0).toLocaleString("es-MX", { minimumFractionDigits: 2 })}`;

// ─── Fila de dato en la tabla de resumen ──────────────────────────────────────
const FilaDato = ({ label, value }) => (
  <tr>
    <td style={{ fontWeight: "bold", whiteSpace: "nowrap", paddingRight: 8 }}>{label}</td>
    <td>{value}</td>
  </tr>
);

// ─── Barra de progreso ────────────────────────────────────────────────────────
const BarraProgreso = ({ porcentajePagado, porcentajePorPagar }) => {
  const pagado = parseFloat(porcentajePagado) || 0;
  const porPagar = parseFloat(porcentajePorPagar) || 0;
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.78em", marginBottom: 2 }}>
        <span style={{ color: "green", fontWeight: "bold" }}>✔ Pagado: {porcentajePagado}</span>
        <span style={{ color: "#c0392b", fontWeight: "bold" }}>Pendiente: {porcentajePorPagar}</span>
      </div>
      <div style={{ height: 10, background: "#e0e0e0", borderRadius: 6, overflow: "hidden" }}>
        <div
          style={{
            height: "100%",
            width: `${pagado}%`,
            background: "linear-gradient(90deg, #27ae60, #2ecc71)",
            borderRadius: 6,
            transition: "width 0.4s",
          }}
        />
      </div>
    </div>
  );
};

// ─── Tabla de pagos de un contrato ───────────────────────────────────────────
const TablaPagos = ({ pagos, mostrarRestante = false }) => (
  <table className="table table-bordered table-sm" style={{ fontSize: "0.82em" }}>
    <thead style={{ background: BRAND_COLOR, color: "white" }}>
      <tr>
        <th className="text-center">Recibo</th>
        <th className="text-center">Abono</th>
        <th className="text-center">Fecha de pago</th>
        {mostrarRestante && <th className="text-center">Saldo restante</th>}
      </tr>
    </thead>
    <tbody>
      {pagos?.map((pago, i) => (
        <tr key={i} className="text-center">
          <td>{pago.serie ?? pago.cseriecobranza?.serie} - {pago.folio}</td>
          <td>{pago.fmMontoPago ?? fmtMonto(pago.monto)}</td>
          <td>{fmtFecha(pago.fechaPago)}</td>
          {mostrarRestante && <td>{pago.restante}</td>}
        </tr>
      ))}
    </tbody>
  </table>
);

export const EstadoDeCuentaImpresionComponent = () => {
  const { user, dispatch } = React.useContext(AuthContext);

  const { numerocontrato } = useParams();
  const [finalData, setFinalData] = useState(null);
  const printRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
  });

  const handlePrintWithData = async () => {
    postGenerarEstadoDeCuentaAPI(user, {
      idcontrato: numerocontrato,
    }).then((response) => {
      if (response.status === 200) {
        setFinalData(response.data);
        setTimeout(() => {
          handlePrint();
        }, 100);
      } else {
        alert("No se pudo obtener la información para imprimir.");
      }
    });
  };

  return (
    <>
      {/* ── Botón de impresión (solo visible en pantalla) ── */}
      <button className="btn btn-warning ms-1" onClick={handlePrintWithData}>
        <FcPrint /> Imprimir Estado de Cuenta
      </button>

      {/* ── Documento imprimible ── */}
      <div className="container-fluid mt-5 d-none d-print-block" ref={printRef} style={{ fontSize: "0.88em" }}>

        {/* ══ ENCABEZADO ══════════════════════════════════════════════════════ */}
        <div className="row align-items-center mb-3" style={{ borderBottom: `3px solid ${BRAND_COLOR}`, paddingBottom: 12 }}>
          {/* Logo + título */}
          <div className="col-5 text-center">
            <img height={70} src={imageVitanova} alt="Vitanova" />
            <h2 style={{ fontSize: "1.3em", fontWeight: "bold", marginTop: 6, marginBottom: 2 }}>
              ESTADO DE CUENTA
            </h2>
            <p style={{ color: "#555", margin: 0 }}>
              Fecha:{" "}
              {new Date().toLocaleDateString("es-ES", {
                day: "2-digit",
                month: "long",
                year: "numeric",
              })}
            </p>
          </div>

          {/* Datos del cliente */}
          <div className="col-7">
            <table className="table table-sm mb-0" style={{ fontSize: "0.95em" }}>
              <tbody>
                <FilaDato label="Contrato:" value={`${finalData?.serie}-${finalData?.noContrato}`} />
                <FilaDato label="Titular:" value={finalData?.titular} />
                <FilaDato
                  label="Domicilio:"
                  value={`${finalData?.domicilio ?? ""} ${finalData?.colonia ?? ""}, CP: ${finalData?.cp ?? ""}`}
                />
                <FilaDato label="Servicio:" value={finalData?.detalleContrato?.servicio?.nombre} />
                <FilaDato
                  label="Serie gestor:"
                  value={
                    finalData?.detalleContrato?.detalleContrato == null
                      ? "SIN SERIE"
                      : finalData?.detalleContrato?.detalleContrato?.serieGestor
                  }
                />
              </tbody>
            </table>
          </div>
        </div>

        {/* ══ RESUMEN FINANCIERO ═══════════════════════════════════════════════ */}
        <div className="row mb-3">
          <div className="col-12">
            <div style={{ background: BRAND_COLOR, color: "white", padding: "4px 10px", fontWeight: "bold", borderRadius: "4px 4px 0 0" }}>
              RESUMEN FINANCIERO
            </div>
            <table className="table table-bordered table-sm mb-0" style={{ borderRadius: "0 0 4px 4px" }}>
              <tbody>
                <tr>
                  <td><strong>Fecha del contrato:</strong> {fmtFecha(finalData?.fechaContrato)}</td>
                  <td><strong>Financiamiento:</strong> {finalData?.detalleContrato?.plazo} meses</td>
                  <td><strong>Pagos realizados:</strong> {finalData?.pagos?.length}</td>
                </tr>
                <tr>
                  <td><strong>Costo total:</strong> {finalData?.importeContrato}</td>
                  <td><strong>Abono a capital:</strong> {finalData?.pagadoHoy}</td>
                  <td><strong>Saldo pendiente:</strong> {finalData?.porPagar}</td>
                </tr>
                {finalData?.pagosDeRestructura && (
                  <tr>
                    <td colSpan="3">
                      <strong>Pagos de restructura aplicados:</strong> {finalData?.pagosDeRestructura}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {/* Barra de avance de pago */}
            <div style={{ border: "1px solid #dee2e6", borderTop: "none", padding: "8px 10px", borderRadius: "0 0 4px 4px" }}>
              <BarraProgreso
                porcentajePagado={finalData?.porcentajePagado}
                porcentajePorPagar={finalData?.porcentajePorPagar}
              />
            </div>

            {/* Monto en atraso */}
            <div style={{ background: "#fdecea", border: "1px solid #f5c6c2", borderRadius: 4, padding: "5px 10px", marginTop: 6, textAlign: "center", color: "#c0392b", fontWeight: "bold" }}>
              Monto en atraso: {finalData?.montoAtraso}
            </div>
          </div>
        </div>


        <div className="row">
          <div className="col-6">
            <img src="https://funerariavitanova.com/ads/estadodecuenta/imagenDerecha.jpg" alt="Contrato reestructurado" style={{ width: '100%', }} />
          </div>
          <div className="col-6">
            <img src="https://funerariavitanova.com/ads/estadodecuenta/imagenIzquierda.jpg" alt="Contrato reestructurado" style={{ width: '100%', }} />
          </div>
        </div>

        {/* ══ MOVIMIENTOS DEL CONTRATO ACTUAL ═════════════════════════════════ */}
        <div className="row mb-3">
          <div className="col-12">
            <div style={{ background: BRAND_COLOR, color: "white", padding: "4px 10px", fontWeight: "bold", borderRadius: "4px 4px 0 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>MOVIMIENTOS — CONTRATO {finalData?.serie}-{finalData?.noContrato}</span>

            </div>
            <TablaPagos pagos={finalData?.pagos} mostrarRestante={true} />
          </div>
        </div>

        {finalData?.detalleRestructura?.length > 0 && (
          <span style={{ background: "#000000", color: "white", borderRadius: 4, padding: "1px 8px", fontSize: "0.85em", fontWeight: "bold", letterSpacing: "0.5px" }}>
            CONTRATO RESTRUCTURADO
          </span>
        )}



        {/* ══ HISTORIAL DE RESTRUCTURA ════════════════════════════════════════ */}
        {finalData?.detalleRestructura?.length > 0 && (
          <div className="row">
            <div className="col-12">
              <div style={{ background: "#555", color: "white", padding: "4px 10px", fontWeight: "bold", borderRadius: "4px 4px 0 0" }}>
                HISTORIAL DE CONTRATOS ANTERIORES (RESTRUCTURA)
              </div>

              {finalData.detalleRestructura.map((contrato, ci) => {
                const totalContrato = contrato.pagos?.reduce(
                  (sum, p) => sum + parseFloat(p.monto || 0), 0
                ) ?? 0;

                return (
                  <div key={ci} style={{ border: "1px solid #bbb", marginBottom: 12, borderRadius: 4, overflow: "hidden" }}>
                    {/* Encabezado del contrato anterior */}
                    <div style={{ background: "#f0f0f0", color: "#222", padding: "6px 10px", display: "flex", gap: 16, flexWrap: "wrap", fontSize: "0.9em", borderBottom: "2px solid #bbb" }}>
                      <span><strong>Contrato:</strong> {contrato.serie}-{contrato.noContrato}</span>
                      <span><strong>Titular:</strong> {contrato.titular}</span>
                      <span><strong>Fecha:</strong> {fmtFecha(contrato.fechaContrato)}</span>
                      <span style={{ marginLeft: "auto", color: "#27ae60", fontWeight: "bold" }}><strong>Total pagado:</strong> {fmtMonto(totalContrato)}</span>
                    </div>

                    {/* Pagos del contrato anterior */}
                    <TablaPagos pagos={contrato.pagos} mostrarRestante={false} />
                  </div>
                );
              })}
            </div>
          </div>
        )}

      </div>
    </>
  );
};
